<template>
  <div
    :class="[
      'progress-expanded',
      embedded ? 'pa-0' : 'pa-0',
      dialogScrollable && 'progress-expanded--dialog'
    ]"
  >
    <div class="progress-expanded__hero bg-primary text-white">
      <div class="d-flex align-center justify-space-between px-4 pt-4">
        <div v-if="showDialogClose" class="text-h6 font-weight-bold text-center flex-grow-1">
          {{ prizeName }}
        </div>

        <v-btn
          v-if="showDialogClose"
          icon
          variant="text"
          size="36"
          class="text-white progress-expanded__close-btn"
          aria-label="Close"
          @click="$emit('close')"
        >
          <icon height="14" width="14" fill="white">close-x</icon>
        </v-btn>

        <v-btn
          v-if="showCollapse"
          icon
          variant="text"
          density="comfortable"
          class="text-white"
          aria-label="Collapse progress"
          @click="$emit('collapse')"
        >
          <v-icon icon="mdi-chevron-up" />
        </v-btn>
      </div>

      <div class="d-flex flex-column align-center px-4 pt-6 pb-4">
        <v-progress-circular
          :model-value="safePercent"
          :size="155"
          :width="12"
          color="white"
          rounded
          class="progress-expanded__progress-circular"
        >
          <div class="text-center progress-expanded__progress-circular-content">
            <div class="font-weight-bold">
              {{ ringValue }}
            </div>
            <div class="text-caption opacity-90 px-3 progress-expanded__progress-circular-content-entries">
              {{ entriesLabel }}
            </div>
          </div>
        </v-progress-circular>

        <div class="d-flex w-100 justify-center mt-4">
          <div class="text-center px-6">
            <div class="text-h5 font-weight-bold">{{ overallRedeemed }}</div>
            <div class="text-caption opacity-90 progress-expanded__divider-text">{{ completedLabel }}</div>
          </div>
          <v-divider vertical class="opacity-40 progress-expanded__divider" />
          <div class="text-center px-6">
            <div class="text-h5 font-weight-bold">{{ overallRemaining }}</div>
            <div class="text-caption opacity-90 progress-expanded__divider-text">{{ remainingLabel }}</div>
          </div>
        </div>

        <!-- <div v-if="subtitle" class="text-body-2 mt-4 text-center opacity-95">
          {{ subtitle }}
        </div> -->
        
      </div>
    </div>

    <!-- List section -->
    <div class="progress-expanded__list">
      <div class="pa-4">
        <div class="text-subtitle-2 font-weight-bold mb-3 text-medium-emphasis">
          Tracked Passes
        </div>

        <progress-deal-item
          v-for="row in trackedDeals"
          :key="row.id"
          :title="row.title"
          :redeemed="row.redeemed"
          :total="row.total"
          :is-expired="row.isExpired"
          :entries-label="entriesLabel"
        />
      </div> 
      <div
        v-if="description"
        class="text-body-2 mt-2 text-center opacity-90 progress-expanded__description"
      >
      {{ description }}
    </div>     
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import ProgressDealItem from './ProgressDealItem.vue';

defineProps({
  showCollapse: {
    type: Boolean,
    default: true
  },
  embedded: {
    type: Boolean,
    default: false
  },
  showDialogClose: {
    type: Boolean,
    default: false
  },
  dialogScrollable: {
    type: Boolean,
    default: false
  }
});

defineEmits(['collapse', 'close']);

const store = useStore();

const config = computed(() => store.state.progressTracker.progressTrackerConfig);

const prizeName = computed(
  () => config.value?.texts?.prize_name || 'Progress'
);

const subtitle = computed(() => config.value?.texts?.subtitle || '');
const description = computed(() => config.value?.texts?.description || '');
const activitiesLabel = computed(
  () => config.value?.texts?.activities_label || ''
);

const fraction = computed(() => store.getters['progressTracker/progressFraction']);
const percent = computed(() => store.getters['progressTracker/progressPercentage']);
const trackedDeals = computed(() => store.getters['progressTracker/trackedDeals']);

const overallRedeemed = computed(() => store.getters['progressTracker/overallRedeemed']);
const overallTotal = computed(() => store.getters['progressTracker/overallTotal']);
const overallRemaining = computed(() => Math.max(0, Number(overallTotal.value || 0) - Number(overallRedeemed.value || 0)));

const safePercent = computed(() => {
  const n = Number(percent.value);
  if (Number.isNaN(n)) return 0;
  return Math.min(100, Math.max(0, n));
});

const fractionCompact = computed(() => String(fraction.value || '').replace(/\s+/g, ''));
const displayFormat = computed(() => config.value?.display_format ?? 'fraction');
const ringValue = computed(() =>
  displayFormat.value === 'percentage'
    ? `${Math.round(safePercent.value)}%`
    : fractionCompact.value
);
const entriesLabel = computed(() => config.value?.texts?.activities_label || 'Entries');
const completedLabel = computed(() => 'Completed');
const remainingLabel = computed(() => 'Remaining');
</script>

<style scoped>
.progress-expanded--dialog {
  display: flex;
  flex-direction: column;
  max-height: 80vh;
}

.progress-expanded--dialog .progress-expanded__list {
  flex: 1 1 auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.progress-expanded__hero {
  padding-bottom: 8px;
}

.progress-expanded__progress-circular :deep(.v-progress-circular__underlay) {
  stroke: rgba(255, 255, 255, 0.35) !important;
}

.progress-expanded__progress-circular :deep(.v-progress-circular__overlay) {
  stroke-linecap: round;
}

.progress-expanded__description {
  white-space: pre-line;
  padding-top: 1rem;
  padding-bottom: 1rem;
  padding-left: .5rem;
  padding-right: .5rem;
  text-align: center;
  font-size: 0.82rem !important;
  font-weight: 400 !important;
  line-height: 1rem !important;
  /* color: rgba(var(--v-theme-on-surface), 0.875); */
  color: white;
  background: rgba(var(--v-theme-primary), 1);
  box-shadow: inset 0 2px 2px -2px rgba(0, 0, 0, 0.4);
}

@media (max-width: 480px) {
  .progress-expanded__description {
    padding-left: .5rem;
    padding-right: .5rem;
  }
}

.progress-expanded__divider {
  background: white;
}

.progress-expanded__divider-text {
  font-size: 0.875rem !important;
  font-weight: 400 !important;
  line-height: 1.25rem !important;
  letter-spacing: 0.0333333333em !important;
}

.progress-expanded__progress-circular-content {
  font-size: 1.58rem !important;
  font-weight: 600 !important;
  line-height: 2rem !important;
  letter-spacing: 0.0333333333em !important;
}

.progress-expanded__progress-circular-content-entries {
  font-size: 0.71rem !important;
  font-weight: 400 !important;
  line-height: 1rem !important;
  letter-spacing: 0 !important;
  word-break: keep-all;
}

.progress-expanded__close-btn {
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.2);
  transition: background-color 0.3s ease;
  &:hover {
    background-color: rgba(255, 255, 255, 0.3);
  }
}
</style>
